import React from 'react';

export const warrenGHarding = {
  presidentNumber: 29,
  president: 'Warren G. Harding',
  subheader: 'Return to Normalcy',
  years: '1921-1923',
  predecessor: 'Woodrow Wilson',
  successor: 'Calvin Coolidge',
  letter: {
    header: 'Letter to Calvin Coolidge',
    content: `My Dear Calvin,
I surrender to you a presidency that has brought me more troubles than I anticipated when I sought this office. The burden of following Wilson's crusading idealism with practical governance has proven more difficult than campaign speeches suggested, and I confess that some of my appointments have brought disgrace upon an administration that promised honesty and competence.

Yet I believe we have restored a measure of normalcy to American life after the tumultuous Wilson years. The separate peace with Germany, our withdrawal from international entanglements, and the reduction of federal spending have allowed the nation to recover from wartime dislocations. America needs time to digest the changes of recent decades before assuming new burdens.

The economic boom we have encouraged through business-friendly policies has vindicated Republican faith in private enterprise. Lower taxes, reduced regulation, and support for American business have unleashed prosperity that benefits all classes. Yet I worry about speculation and the concentration of wealth that may undermine this prosperity's foundations.
My greatest satisfaction has been the Washington Conference on naval disarmament, which has reduced the threat of war while saving taxpayers the expense of naval competition. Perhaps cooperation rather than crusading can achieve the international stability that Wilson sought through more grandiose means.

I have discovered that the presidency isolates its occupant from honest counsel. Surrounded by flatterers and favor-seekers, a president must rely upon his own judgment in matters where he may lack expertise. Choose your associates carefully, for they will determine your legacy as much as your policies.

The scandals that have begun to surface—and I fear more will follow—reflect my poor judgment in trusting old friends who mistook public service for private opportunity. Learn from my mistakes and remember that personal loyalty cannot excuse public corruption.

Your friend and predecessor, Warren G. Harding`
  },
  biographical: [
    'Born November 2, 1865, in Blooming Grove, Ohio',
    'Served partial term, March 4, 1921 – August 2, 1923',
    'Died in office, succeeded by Calvin Coolidge',
    'Previously served as U.S. Senator from Ohio (1915-1921)',
    'Won the largest popular vote margin in history (1920)'
  ],
  presidencyFacts: [
    'Promised "return to normalcy" after Wilson years',
    'Reduced taxes and federal spending',
    'Held Washington Naval Conference (1921-1922)',
    'Pardoned Eugene Debs and other wartime prisoners',
    'Administration marred by corruption scandals'
  ],
  milestones: [
    'Emergency Quota Act passed (1921)',
    'Washington Naval Conference (1921-1922)',
    'Revenue Act reduced taxes (1921)',
    'Teapot Dome scandal emerged (1922)',
    'Died suddenly in San Francisco (1923)'
  ],
  personalLife: [
    'Married Florence Kling DeWolfe in 1891',
    'Had no children with Florence',
    'Had extramarital affairs during presidency',
    'Died on August 2, 1923, in San Francisco',
    'Buried in Marion, Ohio'
  ],
  impactAndLegacy: [
    'Restored conservative Republican policies',
    'Established naval disarmament agreements',
    'Reduced federal government involvement',
    'Administration tarnished by corruption',
    'Ranked among worst presidents by historians'
  ],
  postPresidency: [
    'Died in office on August 2, 1923',
    'Scandals fully revealed after his death',
    'Teapot Dome scandal dominated his legacy',
    'Buried in Marion, Ohio',
    'Widow Florence lived until 1924'
  ],
  historicalAnalysis: `Historical Analysis of Harding's Presidency (1921-1923)
Warren G. Harding's brief presidency represented a reaction against Wilsonian progressivism and internationalism, emphasizing business-friendly policies and diplomatic restraint while being overshadowed by corruption scandals that emerged during and after his administration.

[SECTION] Return to Normalcy and Post-War Adjustment

Harding's campaign promise to return America to "normalcy" resonated with voters exhausted by Wilson's crusades and wartime restrictions. His landslide victory in 1920 represented a decisive rejection of Democratic internationalism and progressive reform.

The administration's domestic policies emphasized reducing federal spending, cutting taxes, and eliminating wartime economic controls. These measures helped facilitate the transition from a wartime to peacetime economy while reducing federal interference in business operations.

Harding's support for immigration restriction, culminating in the Emergency Quota Act of 1921 and later the National Origins Act, reflected nativist sentiment and marked a decisive break with America's traditional open immigration policy.

The president's pardoning of Eugene Debs and other wartime political prisoners demonstrated his commitment to healing divisions created by Wilson's wartime prosecutions, though this clemency was controversial among conservatives.

[SECTION] Economic Policy and Business Partnership

Harding's economic policies, largely shaped by Treasury Secretary Andrew Mellon, emphasized tax reduction and business deregulation. The Revenue Act of 1921 reduced the top income tax rate from 73% to 58%, beginning a decade-long trend toward lower taxation.

The administration's pro-business approach contributed to economic growth that would characterize the 1920s, though critics argued that tax cuts primarily benefited wealthy Americans while increasing income inequality.

Harding supported protective tariffs through the Emergency Tariff Act and later the Fordney-McCumber Tariff, reversing Wilson's move toward lower rates and protecting American industry from foreign competition.

The creation of the Bureau of the Budget (1921) represented a significant administrative reform that improved federal financial management and established systematic budgeting procedures still used today.

[SECTION] Foreign Policy and Isolationism

Harding's foreign policy emphasized unilateral American action rather than multilateral cooperation, formally ending American involvement in Wilson's internationalist experiments while maintaining commercial and diplomatic relations.

The separate peace treaties with Germany, Austria, and Hungary formally ended American participation in World War I without requiring Senate ratification of the Versailles Treaty, satisfying Republican demands for independence from European entanglements.

The Washington Naval Conference (1921-1922) represented Harding's greatest diplomatic achievement, establishing naval arms limitation agreements that reduced international tensions while saving American taxpayers significant defense spending.

The Four-Power Treaty with Britain, France, and Japan established mutual respect for Pacific territories, while the Nine-Power Treaty guaranteed Chinese territorial integrity and commercial access, addressing Asian tensions without military commitments.

[SECTION] Administrative Appointments and the "Ohio Gang"

Harding's cabinet included several distinguished appointees, including Secretary of State Charles Evans Hughes, Treasury Secretary Andrew Mellon, and Commerce Secretary Herbert Hoover, who provided competent leadership in their respective areas.

However, Harding's loyalty to old friends from Ohio created opportunities for corruption among the so-called "Ohio Gang"—cronies who used federal positions for personal enrichment rather than public service.

The Veterans' Bureau scandal involved Director Charles Forbes's systematic theft of government funds intended for veterans' care, representing a particularly egregious betrayal of public trust.

Attorney General Harry Daugherty's involvement in various corrupt schemes, including the sale of pardons and government contracts, further tarnished the administration's reputation and led to his eventual resignation.

[SECTION] The Teapot Dome Scandal

The Teapot Dome scandal, which fully emerged after Harding's death, involved Interior Secretary Albert Fall's secret leasing of federal oil reserves to private companies in exchange for personal payments and loans.

Fall's conviction for bribery made him the first cabinet member imprisoned for crimes committed in office, symbolizing the corruption that characterized Harding's administration despite the president's personal honesty.

The scandal demonstrated the dangers of appointing unqualified loyalists to important positions and highlighted the need for stronger oversight of federal resources and contract procedures.

[SECTION] Social and Cultural Issues

Harding's presidency coincided with significant social changes, including the enforcement of Prohibition, the revival of the Ku Klux Klan, and growing cultural tensions between urban and rural America.

The administration's enforcement of Prohibition was inconsistent and often ineffective, contributing to the rise of organized crime and widespread disrespect for federal law that would characterize the entire decade.

Harding's opposition to federal anti-lynching legislation disappointed African Americans and civil rights advocates, reflecting his party's retreat from Reconstruction-era commitments to racial equality.

[SECTION] Death and Succession

Harding's sudden death in San Francisco on August 2, 1923, while returning from an Alaska trip, created speculation about the causes and circumstances of his demise. Official reports cited heart problems and pneumonia, though some contemporaries suspected suicide or poisoning.

The president's death came as congressional investigations were beginning to uncover the administration's scandals, leading to speculation that knowledge of coming revelations contributed to his decline.

Calvin Coolidge's succession to the presidency provided continuity for Republican policies while distancing the party from Harding's scandals through Coolidge's reputation for personal integrity.

[SECTION] Historical Assessment

Historical evaluation of Harding has been consistently negative, with most surveys ranking him among the worst presidents due to the scandals that characterized his administration and his apparent inability to control subordinates.

However, some recent scholarship has attempted to rehabilitate Harding's reputation by emphasizing his policy achievements, including tax reduction, administrative reform, and diplomatic successes like the Washington Naval Conference.

Most historians conclude that while Harding was personally honest and well-intentioned, his poor judgment in appointments and his hands-off administrative style enabled corruption that severely damaged public confidence in government.

The Harding presidency demonstrated the importance of presidential oversight and the selection of qualified, ethical appointees, lessons that influenced future administrations' personnel practices.`,
  sources: [
    '[LINK:https://millercenter.org/president/harding/impact-and-legacy|Miller Center, University of Virginia. "Warren G. Harding: Impact and Legacy." Accessed 2025.]',
    '[LINK:https://www.history.com/topics/us-presidents/warren-g-harding|History.com. "Warren G. Harding: Facts, Presidency & Death." Updated February 2025.]',
    '[LINK:https://www.whitehousehistory.org/bios/warren-g-harding|White House Historical Association. "Warren G. Harding." Accessed 2025.]',
    '[LINK:https://en.wikipedia.org/wiki/Presidency_of_Warren_G._Harding|Wikipedia. "Presidency of Warren G. Harding." Updated 2025.]',
    '[LINK:https://archive.org/details/hardingera00murr|Murray, Robert K. The Harding Era: Warren G. Harding and His Administration. University of Minnesota Press, 1969.]',
    '[LINK:https://archive.org/details/shadowofblooming00russ|Russell, Francis. The Shadow of Blooming Grove: Warren G. Harding in His Times. McGraw-Hill, 1968.]',
    '[LINK:https://archive.org/details/strangedeathsofpr00ferr|Ferrell, Robert H. The Strange Deaths of President Harding. University of Missouri Press, 1996.]'
  ]
};

export default warrenGHarding;